import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  DispatcherNotification,
  NotificationsData,
  NotificationSettings,
  NotificationFilter,
  NotificationType,
} from '../models/notification.model';
import { ApiResponse } from '../interfaces';

/**
 * NotificationsService
 * 
 * Service for dispatcher notifications
 * Handles real-time notifications, settings, and browser notifications
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private readonly apiUrl = `${environment.apiUrl}/company/notifications`;

  constructor(private http: HttpClient) {}

  /**
   * Get notifications list with filters
   * 
   * @param filters - Notification filters
   * @param page - Page number
   * @param limit - Items per page
   */
  getNotifications(
    filters?: NotificationFilter,
    page: number = 1,
    limit: number = 50
  ): Observable<ApiResponse<NotificationsData>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (filters) {
      // Type filter (can be single or multiple)
      if (filters.type) {
        const types = Array.isArray(filters.type) 
          ? filters.type.join(',') 
          : filters.type;
        params = params.set('type', types);
      }

      // Priority filter
      if (filters.priority) {
        params = params.set('priority', filters.priority);
      }

      // Read status filter
      if (filters.isRead !== undefined) {
        params = params.set('isRead', filters.isRead.toString());
      }

      // Date range
      if (filters.dateFrom) {
        params = params.set('dateFrom', filters.dateFrom.toISOString());
      }
      if (filters.dateTo) {
        params = params.set('dateTo', filters.dateTo.toISOString());
      }
    }

    return this.http.get<ApiResponse<NotificationsData>>(this.apiUrl, { params });
  }

  /**
   * Get unread notifications count
   */
  getUnreadCount(): Observable<ApiResponse<{ count: number }>> {
    return this.http.get<ApiResponse<{ count: number }>>(`${this.apiUrl}/unread-count`);
  }

  /**
   * Mark notification as read
   * 
   * @param notificationId - Notification ID
   */
  markAsRead(notificationId: number): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      `${this.apiUrl}/${notificationId}/read`,
      {}
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/read-all`, {});
  }

  /**
   * Delete notification
   * 
   * @param notificationId - Notification ID
   */
  deleteNotification(notificationId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${notificationId}`);
  }

  /**
   * Delete all read notifications
   */
  deleteAllRead(): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/read`);
  }

  /**
   * Get notification settings
   */
  getSettings(): Observable<ApiResponse<NotificationSettings>> {
    return this.http.get<ApiResponse<NotificationSettings>>(`${this.apiUrl}/settings`);
  }

  /**
   * Update notification settings
   * 
   * @param settings - Notification settings
   */
  updateSettings(settings: NotificationSettings): Observable<ApiResponse<NotificationSettings>> {
    return this.http.put<ApiResponse<NotificationSettings>>(
      `${this.apiUrl}/settings`,
      settings
    );
  }

  /**
   * Send test notification (for testing sound/browser notifications)
   * 
   * @param type - Notification type
   */
  sendTestNotification(type: NotificationType): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/test`, { type });
  }

  /**
   * Request browser notification permission
   */
  requestBrowserPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return Promise.resolve('denied');
    }

    if (Notification.permission === 'granted') {
      return Promise.resolve('granted');
    }

    if (Notification.permission !== 'denied') {
      return Notification.requestPermission();
    }

    return Promise.resolve('denied');
  }

  /**
   * Show browser notification
   * 
   * @param notification - Notification data
   */
  showBrowserNotification(notification: DispatcherNotification): void {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const options: NotificationOptions = {
      body: notification.message,
      icon: '/assets/icons/icon-192x192.png',
      badge: '/assets/icons/icon-72x72.png',
      tag: `notification-${notification.id}`,
      requireInteraction: notification.priority === 'critical',
      data: {
        notificationId: notification.id,
        orderId: notification.orderId,
        driverId: notification.driverId,
      }
    };

    const browserNotification = new Notification(notification.title, options);

    // Handle notification click
    browserNotification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      
      // Navigate to order or driver details if applicable
      if (notification.orderId) {
        // Router navigation will be handled by component
        window.dispatchEvent(new CustomEvent('notification-click', {
          detail: { orderId: notification.orderId }
        }));
      }

      browserNotification.close();
    };
  }

  /**
   * Play notification sound
   * 
   * @param soundFile - Sound file name (optional)
   */
  playNotificationSound(soundFile: string = 'notification.mp3'): void {
    try {
      const audio = new Audio(`/assets/sounds/${soundFile}`);
      audio.volume = 0.5;
      audio.play().catch(error => {
      });
    } catch (error) {
    }
  }

  /**
   * Get notifications by type
   * 
   * @param type - Notification type
   * @param page - Page number
   * @param limit - Items per page
   */
  getNotificationsByType(
    type: NotificationType,
    page: number = 1,
    limit: number = 20
  ): Observable<ApiResponse<NotificationsData>> {
    return this.getNotifications({ type }, page, limit);
  }

  /**
   * Get critical notifications
   * 
   * @param page - Page number
   * @param limit - Items per page
   */
  getCriticalNotifications(
    page: number = 1,
    limit: number = 20
  ): Observable<ApiResponse<NotificationsData>> {
    return this.getNotifications({ priority: 'critical' }, page, limit);
  }
}
